import React from 'react';
import { T, NUMERIC, FONT_STACK, money } from '../customer/customers.theme';
import { ChevronLeftIcon, ChevronRightIcon } from './OrdersIcons';
import { isInvoiceOrder, isSettled, itemCount, orderTime } from './recentOrdersApi';

/**
 * A waiter's own orders from today, as a strip above the order-type row.
 *
 * What it is for: a waiter standing at a table who needs to add two more teas
 * to an order they rang in five minutes ago, or hand over a receipt again.
 * Both of those used to mean finding a manager, because a waiter cannot reach
 * the Orders screen.
 *
 * Horizontal because it sits inside the cart panel, which is about 400px wide
 * and has the actual order to show underneath. Cards are small on purpose:
 * enough to recognise an order by its number, its time and its total, and no
 * more. Anything else is a tap away in the dialog.
 */

const COPY = {
  title: 'Recent orders',
  today: 'Today',
  empty: 'No orders yet',
  loading: 'Loading…',
  paid: 'PAID',
};

const CARD_WIDTH = 152;

export default function RecentOrders({ orders, loading, editingOrderId, onOpen }) {
  const railRef = React.useRef(null);
  const [overflow, setOverflow] = React.useState({ left: false, right: false });

  /**
   * Whether either arrow has anywhere to go.
   *
   * Measured rather than assumed, so a waiter with two orders does not get
   * arrows that do nothing. Re-measured on scroll and whenever the list
   * changes, which covers a card arriving after a refetch.
   */
  const measure = React.useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;

    setOverflow({
      left: rail.scrollLeft > 4,
      right: rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 4,
    });
  }, []);

  React.useEffect(() => {
    measure();
  }, [measure, orders]);

  const nudge = (direction) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({ left: direction * (CARD_WIDTH + 10) * 2, behavior: 'smooth' });
  };

  return (
    <div style={{ marginBottom: 16, fontFamily: FONT_STACK }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          paddingBottom: 8,
        }}
      >
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: T.ink }}>
          {COPY.title}
        </span>
        <span style={{ fontSize: 11.5, color: T.inkFaint }}>
          {loading ? COPY.loading : COPY.today}
        </span>
      </div>

      {!loading && !orders.length ? (
        <div
          style={{
            padding: '14px 0',
            fontSize: 12.5,
            color: T.inkFaint,
          }}
        >
          {COPY.empty}
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <div
            ref={railRef}
            onScroll={measure}
            style={{
              display: 'flex',
              gap: 10,
              overflowX: 'auto',
              scrollSnapType: 'x proximity',
              // Firefox and the standard property; Chrome gets the pseudo
              // element it ignores here, so the rail just keeps a thin bar.
              scrollbarWidth: 'thin',
              paddingBottom: 6,
            }}
          >
            {loading && !orders.length
              ? [0, 1, 2].map((key) => <SkeletonCard key={key} />)
              : orders.map((order) => (
                  <OrderCard
                    key={order._id}
                    order={order}
                    active={editingOrderId === order._id}
                    onOpen={onOpen}
                  />
                ))}
          </div>

          {overflow.left ? <Arrow side="left" onClick={() => nudge(-1)} /> : null}
          {overflow.right ? <Arrow side="right" onClick={() => nudge(1)} /> : null}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, active, onOpen }) {
  const [hovered, setHovered] = React.useState(false);

  const invoice = isInvoiceOrder(order);
  const settled = isSettled(order);

  return (
    <button
      type="button"
      onClick={() => onOpen(order)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 3,
        flexShrink: 0,
        width: CARD_WIDTH,
        padding: '10px 12px',
        scrollSnapAlign: 'start',
        // The order currently loaded on the pad wears the selected state, so a
        // waiter mid-edit can see which one they are changing.
        background: active || invoice ? T.selectedBg : hovered ? T.hover : T.surface,
        border: `1px solid ${
          active ? T.brand : invoice ? T.selectedBorder : T.border
        }`,
        borderRadius: T.radiusPanel,
        font: 'inherit',
        fontFamily: FONT_STACK,
        textAlign: 'left',
        cursor: 'pointer',
        boxSizing: 'border-box',
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
        }}
      >
        <span
          style={{
            flex: 1,
            fontSize: 13.5,
            fontWeight: 700,
            color: invoice || active ? T.brandInk : T.ink,
            ...NUMERIC,
          }}
        >
          #{order.orderNumber}
        </span>

        {/* Settled orders can be looked at and reprinted but not changed, so
            they say so rather than offering an Edit that then refuses. */}
        {settled ? (
          <span
            style={{
              padding: '1px 6px',
              background: T.paidTint,
              borderRadius: T.radiusPill,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: 0.3,
              color: T.paid,
            }}
          >
            {COPY.paid}
          </span>
        ) : null}
      </span>

      <span style={{ fontSize: 11.5, color: T.inkFaint, ...NUMERIC }}>
        {orderTime(order)} · {itemCount(order)}
      </span>

      <span
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: T.ink,
          ...NUMERIC,
        }}
      >
        {money(order.total)}
      </span>
    </button>
  );
}

function SkeletonCard() {
  return (
    <span
      style={{
        flexShrink: 0,
        width: CARD_WIDTH,
        height: 72,
        background: T.shade,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusPanel,
      }}
    />
  );
}

/** Shown only when the rail actually has somewhere to scroll. */
function Arrow({ side, onClick }) {
  return (
    <button
      type="button"
      aria-label={side === 'left' ? 'Scroll back' : 'Scroll forward'}
      onClick={onClick}
      style={{
        position: 'absolute',
        top: '50%',
        [side]: -6,
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
        background: T.surface,
        border: `1px solid ${T.borderStrong}`,
        borderRadius: T.radiusPill,
        color: T.inkSoft,
        cursor: 'pointer',
        padding: 0,
        boxShadow: '0 2px 8px rgba(21,24,29,0.10)',
      }}
    >
      {side === 'left' ? (
        <ChevronLeftIcon size={15} width={2} />
      ) : (
        <ChevronRightIcon size={15} width={2} />
      )}
    </button>
  );
}
